import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Mail, Loader2, Check, AlertTriangle, Send, Trash2, Server, Lock, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const SECURE_OPTIONS = [
  { value: 'starttls', label: 'STARTTLS (recommandé)' },
  { value: 'ssl', label: 'SSL / TLS implicite' },
  { value: 'none', label: 'Aucun chiffrement (déconseillé)' },
];

export default function CompanyEmailSettings() {
  const [presets, setPresets] = useState({});
  const [config, setConfig] = useState(null); // config existante (avec mots de passe masqués)
  const [form, setForm] = useState({
    provider: 'gmail',
    transport: 'smtp',
    host: 'smtp.gmail.com',
    port: 587,
    secure: 'starttls',
    username: '',
    password: '',
    senderEmail: '',
    senderName: 'HR Engine',
    graphTenantId: '',
    graphClientId: '',
    graphClientSecret: '',
    enabled: true,
  });
  const [testRecipient, setTestRecipient] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [pRes, cRes] = await Promise.all([
        api.get('/company/email-config/presets'),
        api.get('/company/email-config'),
      ]);
      setPresets(pRes.data?.presets || {});
      const cfg = cRes.data?.config;
      setConfig(cfg);
      if (cfg) {
        setForm((f) => ({
          ...f,
          ...cfg,
          // password / graphClientSecret restent vides (les masques côté UI),
          // backend les conserve si on ne les renvoie pas.
          password: '',
          graphClientSecret: '',
        }));
      }
    } catch (e) {
      toast.error('Impossible de charger la configuration email');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  const applyPreset = (key) => {
    const p = presets[key] || {};
    setForm((f) => ({
      ...f,
      provider: key,
      transport: p.transport || 'smtp',
      host: p.host ?? f.host,
      port: p.port ?? f.port,
      secure: p.secure ?? f.secure,
      username: key === 'sendgrid' ? 'apikey' : f.username,
    }));
  };

  const handleSave = async () => {
    if (!form.senderEmail) {
      toast.error('L\'adresse expéditeur est requise');
      return;
    }
    setSaving(true);
    try {
      await api.put('/company/email-config', form);
      toast.success('Configuration email enregistrée');
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testRecipient) {
      toast.error('Saisissez un destinataire pour le test');
      return;
    }
    setTesting(true);
    try {
      const r = await api.post('/company/email-config/test', {
        recipient: testRecipient,
        config: form,
      });
      toast.success(r.data?.message || 'Email de test envoyé');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test échoué — vérifiez vos paramètres');
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer la configuration email de l\'entreprise ?\nLes notifications utiliseront la configuration par défaut de la plateforme.')) return;
    try {
      await api.delete('/company/email-config');
      toast.success('Configuration supprimée');
      setConfig(null);
      setForm((f) => ({ ...f, password: '', graphClientSecret: '' }));
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const isGraph = form.transport === 'graph';

  return (
    <div className="space-y-6">
      <Card data-testid="company-email-settings">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-indigo-600" />
            Configuration email de l'entreprise
          </CardTitle>
          <CardDescription>
            Choisissez votre serveur d'envoi pour que les notifications HR Engine
            (rappels d'évaluation, comptes créés, etc.) partent depuis votre domaine.
            {config?.enabled && (
              <span className="ml-2 inline-flex items-center gap-1 text-emerald-700 font-medium">
                <ShieldCheck className="w-4 h-4" /> Active
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement…
            </div>
          ) : (
            <>
              {/* Provider preset */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fournisseur</Label>
                  <Select value={form.provider} onValueChange={applyPreset}>
                    <SelectTrigger data-testid="email-provider-select">
                      <SelectValue placeholder="Choisir un fournisseur" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(presets).map(([k, p]) => (
                        <SelectItem key={k} value={k}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {presets[form.provider]?.hint && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-1">
                      <AlertTriangle className="inline w-3 h-3 mr-1" />
                      {presets[form.provider].hint}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Transport</Label>
                  <Select
                    value={form.transport}
                    onValueChange={(v) => setForm((f) => ({ ...f, transport: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="smtp">SMTP standard</SelectItem>
                      <SelectItem value="graph">Microsoft Graph API</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Sender info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Adresse expéditeur (From) *</Label>
                  <Input
                    type="email"
                    value={form.senderEmail}
                    onChange={(e) => setForm((f) => ({ ...f, senderEmail: e.target.value }))}
                    placeholder="rh@votreentreprise.com"
                    data-testid="email-sender-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nom affiché</Label>
                  <Input
                    value={form.senderName}
                    onChange={(e) => setForm((f) => ({ ...f, senderName: e.target.value }))}
                    placeholder="HR — Mon Entreprise"
                  />
                </div>
              </div>

              {/* SMTP settings */}
              {!isGraph && (
                <Card className="bg-slate-50 border-slate-200">
                  <CardContent className="pt-4 space-y-4">
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="space-y-2 md:col-span-2">
                        <Label className="flex items-center gap-1"><Server className="w-3 h-3" /> Hôte SMTP</Label>
                        <Input
                          value={form.host || ''}
                          onChange={(e) => setForm((f) => ({ ...f, host: e.target.value }))}
                          placeholder="smtp.gmail.com"
                          data-testid="email-host"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Port</Label>
                        <Input
                          type="number"
                          value={form.port || ''}
                          onChange={(e) => setForm((f) => ({ ...f, port: parseInt(e.target.value) || 0 }))}
                          placeholder="587"
                          data-testid="email-port"
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Sécurité</Label>
                        <Select
                          value={form.secure}
                          onValueChange={(v) => setForm((f) => ({ ...f, secure: v }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {SECURE_OPTIONS.map((s) => (
                              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Nom d'utilisateur</Label>
                        <Input
                          value={form.username || ''}
                          onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                          placeholder="(souvent identique à l'expéditeur)"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1">
                          <Lock className="w-3 h-3" /> Mot de passe
                          {config?._passwordIsSet && (
                            <span className="text-xs text-emerald-700 font-normal">· déjà enregistré</span>
                          )}
                        </Label>
                        <Input
                          type="password"
                          value={form.password}
                          onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                          placeholder={config?._passwordIsSet ? '••••••••' : 'Mot de passe ou app password'}
                          data-testid="email-password"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Microsoft Graph settings */}
              {isGraph && (
                <Card className="bg-slate-50 border-slate-200">
                  <CardContent className="pt-4 space-y-4">
                    <p className="text-xs text-slate-600">
                      Créez une <strong>App Registration</strong> dans Azure AD avec la permission
                      <code className="mx-1 bg-slate-200 px-1 rounded">Mail.Send</code> (application).
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Tenant ID</Label>
                        <Input
                          value={form.graphTenantId || ''}
                          onChange={(e) => setForm((f) => ({ ...f, graphTenantId: e.target.value }))}
                          placeholder="00000000-0000-0000-0000-000000000000"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Client ID</Label>
                        <Input
                          value={form.graphClientId || ''}
                          onChange={(e) => setForm((f) => ({ ...f, graphClientId: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label className="flex items-center gap-1">
                          <Lock className="w-3 h-3" /> Client Secret
                          {config?._graphClientSecretIsSet && (
                            <span className="text-xs text-emerald-700 font-normal">· déjà enregistré</span>
                          )}
                        </Label>
                        <Input
                          type="password"
                          value={form.graphClientSecret}
                          onChange={(e) => setForm((f) => ({ ...f, graphClientSecret: e.target.value }))}
                          placeholder={config?._graphClientSecretIsSet ? '••••••••' : ''}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Enable / disable */}
              <div className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-4 py-3">
                <div>
                  <p className="font-medium text-slate-900">Activer cette configuration</p>
                  <p className="text-xs text-slate-500">Désactiver redirige les notifications vers la configuration par défaut.</p>
                </div>
                <Switch
                  checked={!!form.enabled}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))}
                />
              </div>

              {/* Test */}
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-4 space-y-3">
                  <p className="font-medium text-blue-900">1. Tester la configuration</p>
                  <p className="text-xs text-blue-800">
                    Envoyez un email de test pour valider vos paramètres avant de sauvegarder.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      type="email"
                      value={testRecipient}
                      onChange={(e) => setTestRecipient(e.target.value)}
                      placeholder="adresse-test@votreentreprise.com"
                      className="flex-1"
                      data-testid="email-test-recipient"
                    />
                    <Button
                      onClick={handleTest}
                      disabled={testing || !testRecipient}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="email-test-btn"
                    >
                      {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Envoyer un test
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2 border-t">
                {config && (
                  <Button variant="outline" onClick={handleDelete} className="text-red-600 hover:text-red-700">
                    <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                  </Button>
                )}
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="email-save-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                  2. Enregistrer la configuration
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
